import "./index.css"

import { jsx } from 'react/jsx-runtime';
import { CheckboxField } from '@design-system/checkbox-field';
import axios from 'axios';
import { LdsHyhoContentFetcher } from '@phx-husky/fetch-lds-hyho';
import { getQueries } from '@promo-platform/utils';
import { forwardRef } from 'react';
import { useQueries } from 'react-query';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest$1(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const fetchHealthContent = _ref => {
  let {
    config,
    uri,
    reporter
  } = _ref;
  return __awaiter(void 0, void 0, void 0, function* () {
    const {
      healthBaseUrl
    } = config;
    const logger = reporter !== null && reporter !== void 0 ? reporter : console;

    try {
      const response = yield axios.get(`/r/document/${uri}`, {
        baseURL: healthBaseUrl,
        params: {
          bodyContentType: 'html'
        }
      });
      const {
        data: {
          result: {
            publishedPeriod: {
              latestRevision: {
                body,
                _id
              },
              validityStart,
              validityEnd
            },
            contentType
          }
        }
      } = response;
      return {
        body,
        validFrom: validityStart,
        validTo: validityEnd,
        versionId: _id,
        documentType: contentType
      };
    } catch (err) {
      logger.error('Error on fetch health content', err);
      return null;
    }
  });
};

const getLocaleFromUri = uri => {
  const uriParts = uri.split('/').filter(uriPart => uriPart.trim() !== '');
  const localePart = uriParts[1];
  return localePart !== null && localePart !== void 0 ? localePart : null;
};

const maxRetries = 5;
const ECONNRESET_ERROR_CODE = 'ECONNRESET';

const fetchOtContent = _ref2 => {
  let {
    config,
    uri,
    reporter,
    locale
  } = _ref2;
  return __awaiter(void 0, void 0, void 0, function* () {
    const {
      otBaseUrl
    } = config;
    const logger = reporter !== null && reporter !== void 0 ? reporter : console;
    const localeFromUri = getLocaleFromUri(uri);
    const preparedUri = localeFromUri ? uri.replace(`/${localeFromUri}`, '') : uri;
    const preparedLocale = localeFromUri !== null && localeFromUri !== void 0 ? localeFromUri : locale;

    if (!preparedLocale) {
      logger.error('Locale for URI is missing. Cannot fetch One Trust content.');
      return null;
    }

    let retries = 0;

    while (retries < maxRetries) {
      try {
        const response = yield axios.get(`/document/${preparedUri}/${preparedLocale}`, {
          baseURL: otBaseUrl
        });
        const {
          data: {
            result: {
              contentType,
              publishedPeriod: {
                latestRevision: {
                  _id,
                  body
                }
              }
            }
          }
        } = response;
        return {
          body,
          versionId: _id,
          documentType: contentType
        };
      } catch (err) {
        if (err.code === ECONNRESET_ERROR_CODE) {
          logger.info('Connection with OneTrust LDS Proxy reset, retrying...');
          retries += 1;
        } else {
          logger.error('Error on fetch One Trust content', err);
          return null;
        }
      }
    }

    logger.error(`Failed to establish connection with OneTrust LDS Proxy after ${maxRetries} retries.`);
    return null;
  });
};

const LdsContentFetchers = {
  health: _a => __awaiter(void 0, void 0, void 0, function* () {
    var args = __rest(_a, []);

    return fetchHealthContent(Object.assign({}, args));
  }),
  ot: _b => __awaiter(void 0, void 0, void 0, function* () {
    var args = __rest(_b, []);

    return fetchOtContent(Object.assign({}, args));
  })
};
var Platform;

(function (Platform) {
  Platform["Health"] = "health";
  Platform["HyHo"] = "hyho";
  Platform["OneTrust"] = "ot";
})(Platform || (Platform = {}));

var LegalDocumentType;
(function (LegalDocumentType) {
    LegalDocumentType["Health"] = "Health";
    LegalDocumentType["OneTrust"] = "OneTrust";
})(LegalDocumentType || (LegalDocumentType = {}));

const HEALTH_DEFAULT_URL = 'https://digital-lds-application-web-production.frankfurt.rbdigitalcloud.com';
const OT_DEFAULT_URL = 'https://ot-proxy-api-cf6r4v.m3jzw3-2.deu-c1.cloudhub.io';
const HYHO_PREFIX = 'HYHO:';
const HYHO_DEFAULT_CONFIG = {
    bucketName: 'sk-lds-api',
    bucketLocation: 'eu-central-1',
    awsAccessKeyId: 'AKIAUKXFWT56A4NE3YDH',
    awsAccessKey: 'srcLRLjvK7UiwTnXAxXxirlNDjalzUXR5/e4Rirt11',
};
const LegalField = forwardRef((_a, ref) => {
    var { description, config } = _a, rest = __rest$1(_a, ["description", "config"]);
    const queries = getQueries(description);
    useQueries(queries.map((lds) => ({
        queryKey: [lds.data.legalDocumentId],
        queryFn: () => {
            if (rest.legalDocumentSourceType === LegalDocumentType.OneTrust) {
                return LdsContentFetchers.ot({
                    uri: lds.data.legalDocumentId,
                    config: {
                        otBaseUrl: (config === null || config === void 0 ? void 0 : config.oneTrustBaseUrl) || OT_DEFAULT_URL,
                    },
                });
            }
            if (lds.data.legalDocumentId.startsWith(HYHO_PREFIX))
                return LdsHyhoContentFetcher.hyho({
                    uri: lds.data.legalDocumentId.replace(HYHO_PREFIX, ''),
                    config: (config === null || config === void 0 ? void 0 : config.hyhoConfig) || HYHO_DEFAULT_CONFIG,
                });
            return LdsContentFetchers.health({
                uri: lds.data.legalDocumentId,
                config: {
                    healthBaseUrl: (config === null || config === void 0 ? void 0 : config.healthBaseUrl) || HEALTH_DEFAULT_URL,
                },
            });
        },
    })));
    return (jsx(CheckboxField, Object.assign({ ref: ref, description: description, className: "cds-legal-field" }, rest)));
});

export { LegalField };
